/**
 * Hook pour créer des schémas de validation Yup multilingues
 *
 * Génère automatiquement les validations pour les champs traduits
 * en appliquant les mêmes contraintes sur toutes les langues activées.
 *
 * @module useMultiLanguageValidation
 */

import * as Yup from 'yup';
import {useMultiLanguage} from './useMultiLanguage';

/**
 * Hook pour créer des validations multilingues
 *
 * @return {Object} Utilitaires de validation multilingue
 */
export const useMultiLanguageValidation = () => {
  const {isEnabled, enabledLanguages, defaultLanguage} = useMultiLanguage();

  /**
   * Crée un schéma de validation pour un champ traduisible
   *
   * @param {string} fieldName - Nom du champ
   * @param {Object} baseValidation - Validation Yup de base à appliquer
   * @param {Object} options - Options de validation
   * @param {boolean} options.required - Si le champ par défaut est requis
   * @param {boolean} options.requiredTranslations - Si les traductions sont requises
   * @return {Object} Schéma Yup pour le champ et ses traductions
   */
  const createTranslatableFieldValidation = (
      fieldName,
      baseValidation,
      {required = false, requiredTranslations = false} = {},
  ) => {
    const validationSchema = {};

    // Validation du champ principal (toujours présent pour compatibilité)
    validationSchema[fieldName] = required ? baseValidation.required() : baseValidation;

    // Si multilingue activé, ajouter les validations pour les traductions
    if (isEnabled) {
      const translationsValidation = {};

      enabledLanguages.forEach((langCode) => {
        if (langCode === defaultLanguage) {
          // Langue par défaut : reprend les mêmes règles que le champ principal
          translationsValidation[langCode] = required ? baseValidation.required() : baseValidation;
        } else {
          // Autres langues : optionnelles par défaut, sauf si explicitement requises
          // Transform convertit les chaînes vides en null pour bypasser les validations min/max
          translationsValidation[langCode] = requiredTranslations ?
            baseValidation.required() :
            baseValidation
                .nullable()
                .optional()
                .transform((value, original) => (original === '' ? null : value));
        }
      });

      validationSchema.translations = Yup.object({
        [fieldName]: Yup.object(translationsValidation),
      });
    }

    return validationSchema;
  };

  /**
   * Crée un schéma de validation complet pour un formulaire avec champs traduisibles
   *
   * @param {Object} fieldDefinitions - Définitions des champs traduisibles
   * @param {Object} additionalValidations - Validations supplémentaires non-traduisibles
   * @return {Yup.ObjectSchema} Schéma de validation complet
   *
   * @example
   * const schema = createMultiLanguageSchema({
   *   name: {
   *     validation: Yup.string().max(100),
   *     required: true
   *   },
   *   description: {
   *     validation: Yup.string().max(500),
   *     required: false
   *   }
   * }, {
   *   unitPrice: Yup.number().min(0).required()
   * });
   */
  const createMultiLanguageSchema = (fieldDefinitions, additionalValidations = {}) => {
    const schemaFields = {...additionalValidations};

    // Si multilingue désactivé, validation simple des champs principaux
    if (!isEnabled) {
      Object.entries(fieldDefinitions).forEach(([fieldName, config]) => {
        schemaFields[fieldName] = config.required ? config.validation.required() : config.validation;
      });
      return Yup.object(schemaFields);
    }

    // Si multilingue activé, structure les traductions correctement
    const translationFields = {};

    Object.keys(fieldDefinitions).forEach((fieldName) => {
      const config = fieldDefinitions[fieldName];
      const langValidations = {};

      enabledLanguages.forEach((langCode) => {
        if (langCode === defaultLanguage) {
          // Langue par défaut : utilise les règles requises
          langValidations[langCode] = config.required ? config.validation.required() : config.validation;
        } else {
          // Autres langues : optionnelles sauf si explicitement requises
          // Transform convertit les chaînes vides en null pour bypasser les validations min/max
          langValidations[langCode] = config.requiredTranslations ?
            config.validation.required() :
            config.validation
                .nullable()
                .optional()
                .transform((value, original) => (original === '' ? null : value));
        }
      });

      translationFields[fieldName] = Yup.object(langValidations);

      // Garde aussi le champ principal pour compatibilité (optionnel)
      // Transform convertit les chaînes vides en null pour bypasser les validations min/max
      schemaFields[fieldName] = config.validation
          .nullable()
          .optional()
          .transform((value, original) => (original === '' ? null : value));
    });

    schemaFields.translations = Yup.object(translationFields);

    return Yup.object(schemaFields);
  };

  /**
   * Valide qu'au moins la langue par défaut est remplie pour un champ requis
   *
   * @param {string} fieldName - Nom du champ à valider
   * @param {string} message - Message d'erreur personnalisé
   * @return {Yup.TestFunction} Fonction de test Yup
   */
  const requireDefaultLanguage = (fieldName, message = 'Ce champ est requis') => {
    return Yup.mixed().test(
        'require-default-language',
        message,
        /* eslint-disable no-invalid-this */
        function() {
          const {translations} = this.parent;

          if (!isEnabled) {
          // Si multilingue désactivé, valide le champ principal
            return Boolean(this.parent[fieldName]);
          }

          // Vérifie que la langue par défaut est remplie
          return Boolean(
              translations?.[fieldName]?.[defaultLanguage] ||
          this.parent[fieldName],
          );
        },
        /* eslint-enable no-invalid-this */
    );
  };

  return {
    createTranslatableFieldValidation,
    createMultiLanguageSchema,
    requireDefaultLanguage,
    isEnabled,
    enabledLanguages,
    defaultLanguage,
  };
};

export default useMultiLanguageValidation;
